'use client';

import { useMemo, useState } from 'react';
import classNames from 'classnames';
import { X, AlertCircle, FileText, Download, FileSpreadsheet } from 'lucide-react';
import { getProductLogo } from '@/utils/productLogos';
import { Dropdown } from '@/components/molecules/Dropdown';
import { exportContractToPdf } from './exportContractPdf';
import { exportContractToExcel } from './exportContractExcel';
import { formatCurrency, formatPeriodDate } from './format';

// ─── Types (exported for consumers & dev preview) ────────────

export interface ContractProduct {
    name: string;
    color: string;
    isMediaProduct: boolean;
    periods: {
        period: string;
        grossBudget: number;
        targetMediaSpend?: number;
    }[];
}

export type DrawerState = 'loading' | 'empty' | 'error' | 'ready';

export interface ContractDetailsDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    state?: DrawerState;
    dealerName?: string;
    contractedProducts?: ContractProduct[];
    error?: { title: string; message: string };
    onRetry?: () => void;
}

// ─── Constants ───────────────────────────────────────────────

const PRODUCT_LOGO_OVERRIDES: Record<string, string> = {
    'Reputation Management': 'reputation-management.svg',
    OTT: 'ott.svg',
    AutoIncent: 'auto-incent.svg',
};

const resolveProductLogo = (name: string): string | null =>
    getProductLogo(name) ?? PRODUCT_LOGO_OVERRIDES[name] ?? null;

// ─── Sub-components ──────────────────────────────────────────

const ProductCard = ({ product }: { product: ContractProduct }) => {
    const logoFile = resolveProductLogo(product.name);
    const period = product.periods[product.periods.length - 1];

    return (
        <div className="rounded-lg border border-[#e4e8ea] bg-white px-4 py-3.5">
            <div className="flex gap-5">
                {/* ── Left: icon + name + date ── */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 mb-1.5">
                        <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${product.color}12` }}
                        >
                            {logoFile ? (
                                <img
                                    src={`/icons/product/${logoFile}`}
                                    alt={product.name}
                                    className="w-[18px] h-[18px] object-contain"
                                />
                            ) : (
                                <span
                                    className="text-[11px] font-bold"
                                    style={{ color: product.color }}
                                >
                                    {product.name.charAt(0)}
                                </span>
                            )}
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-[13px] font-semibold text-[#232f34] truncate leading-tight">
                                {product.name}
                            </h3>
                            <p className="text-[11px] text-[#8a9199] leading-tight mt-0.5">
                                {formatPeriodDate(period.period)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* ── Right: metrics ── */}
                <div className="flex flex-col justify-center items-end gap-2 flex-shrink-0 min-w-[120px]">
                    <div className="text-right">
                        <p className="text-[10px] font-medium text-[#8a9199] leading-none mb-1">
                            Gross Budget
                        </p>
                        <p className="text-[14px] font-bold text-[#232f34] leading-tight tabular-nums">
                            {formatCurrency(period.grossBudget)}
                        </p>
                    </div>

                    {/* {product.isMediaProduct && period.targetMediaSpend != null && (
                        <div className="text-right">
                            <p className="text-[10px] font-medium text-[#8a9199] leading-none mb-1">
                                Target Media Spend
                            </p>
                            <p className="text-[13px] font-semibold text-dashboard-title leading-tight tabular-nums">
                                {formatCurrency(period.targetMediaSpend)}
                            </p>
                        </div>
                    )} */}
                </div>
            </div>
        </div>
    );
};

/** Skeleton card matching the ProductCard layout */
const SkeletonCard = () => (
    <div className="rounded-lg border border-[#e4e8ea] bg-white px-4 py-3.5 animate-pulse">
        <div className="flex gap-5">
            <div className="flex-1">
                <div className="flex items-center gap-2.5 mb-1.5">
                    <div className="w-8 h-8 rounded-lg bg-[#eef0f2]" />
                    <div>
                        <div className="h-3.5 w-24 bg-[#e4e8ea] rounded mb-1.5" />
                        <div className="h-2.5 w-32 bg-[#eef0f2] rounded" />
                    </div>
                </div>
            </div>
            <div className="flex flex-col items-end gap-2 min-w-[120px]">
                <div className="text-right">
                    <div className="h-2 w-16 bg-[#eef0f2] rounded mb-1.5" />
                    <div className="h-3.5 w-20 bg-[#e4e8ea] rounded" />
                </div>
            </div>
        </div>
    </div>
);

/** Loading state — skeletons that match the ready layout */
const LoadingState = () => (
    <div className="px-5 py-4 space-y-2">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
    </div>
);

/** Empty state — no contract data found for this client */
const EmptyState = () => (
    <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 text-center">
        <div className="w-12 h-12 rounded-full bg-[#f3f5f6] flex items-center justify-center mb-4">
            <FileText size={22} className="text-[#6e7880]" />
        </div>
        <p className="text-[14px] font-semibold text-[#232f34] mb-1">No contract details</p>
        <p className="text-[12px] text-[#6e7880] leading-relaxed max-w-[260px]">
            There are no active contract details available for this client at this time.
        </p>
    </div>
);

/** Error state — failed to load contract data */
const ErrorState = ({
    error,
    onRetry,
}: {
    error: { title: string; message: string };
    onRetry?: () => void;
}) => (
    <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 text-center">
        <div className="w-12 h-12 rounded-full bg-[#fdf0ee] flex items-center justify-center mb-4">
            <AlertCircle size={22} className="text-[#c4452d]" />
        </div>
        <p className="text-[14px] font-semibold text-[#232f34] mb-1">{error.title}</p>
        <p className="text-[12px] text-[#6e7880] leading-relaxed max-w-[280px] mb-4">
            {error.message}
        </p>
        {onRetry && (
            <button
                onClick={onRetry}
                className="text-[12px] font-medium text-[#2a6f6b] hover:text-[#1e5753] transition-colors px-3 py-1.5 rounded-md hover:bg-[#f0f7f6]"
            >
                Try again
            </button>
        )}
    </div>
);

// ─── Ready-state body (product list + summary) ──────────────

const ReadyState = ({ products }: { products: ContractProduct[] }) => {
    const overallTotals = useMemo(() => {
        const totalBudget = products.reduce((total, product) => {
            const latestPeriod = product.periods[product.periods.length - 1];
            return total + latestPeriod.grossBudget;
        }, 0);
        return { totalBudget, activeCount: products.length };
    }, [products]);

    return (
        <>
            {/* Summary metadata row */}
            <div className="px-5 pb-3 pt-3.5 flex-shrink-0">
                <div className="flex items-center gap-1.5 text-[12px]">
                    <span className="text-[#8a9199]">Gross Budget</span>
                    <span className="text-dashboard-title font-semibold tabular-nums">
                        {formatCurrency(overallTotals.totalBudget)}
                    </span>
                    <span className="text-[#c5c9cc] mx-0.5">·</span>
                    <span className="text-[#8a9199]">Active Products</span>
                    <span className="text-dashboard-title font-semibold">
                        {overallTotals.activeCount}
                    </span>
                </div>
            </div>

            {/* Product list */}
            <div className="flex-1 overflow-y-auto px-5 pb-4">
                <div className="space-y-2">
                    {products.map((product) => (
                        <ProductCard key={product.name} product={product} />
                    ))}
                </div>
            </div>
        </>
    );
};

// ─── Main Drawer ─────────────────────────────────────────────

export const ContractDetailsDrawer = ({
    isOpen,
    onClose,
    state = 'ready',
    dealerName,
    contractedProducts,
    error,
    onRetry,
}: ContractDetailsDrawerProps) => {
    const products = contractedProducts ?? [];
    const [isExporting, setIsExporting] = useState(false);

    const handleExportPdf = () => {
        setIsExporting(true);
        try {
            exportContractToPdf(products, dealerName);
        } catch (error) {
            console.error('[ContractDetailsDrawer] PDF export failed', error);
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportExcel = async () => {
        setIsExporting(true);
        try {
            await exportContractToExcel(products, dealerName);
        } catch (error) {
            console.error('[ContractDetailsDrawer] Excel export failed', error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            {isOpen && (
                <div className="fixed inset-0 bg-black/25 z-[999]" onClick={onClose} />
            )}

            <div
                className={classNames(
                    'fixed top-0 right-0 h-full w-full sm:w-[460px] bg-white z-[1000]',
                    'shadow-[-3px_0px_46px_0px_rgba(0,0,0,0.12)]',
                    'transform transition-transform duration-300 ease-in-out',
                    'flex flex-col',
                    isOpen ? 'translate-x-0' : 'translate-x-full'
                )}
            >
                {/* ── Header ── */}
                <div className="px-5 pt-5 pb-3.5 border-b border-[#e4e8ea] flex-shrink-0">
                    <div className="flex items-start justify-between">
                        <h2 className="text-[17px] font-semibold text-[#232f34] leading-tight">
                            Current Contract Details
                        </h2>
                        <div className="flex items-center gap-1 -mt-0.5">
                            {state === 'ready' && products.length > 0 && (
                                <Dropdown
                                    align="right"
                                    trigger={
                                        <button
                                            disabled={isExporting}
                                            className="flex items-center justify-center w-7 h-7 rounded-md hover:bg-[#f3f5f6] text-[#6d7d8a] transition-colors flex-shrink-0 disabled:opacity-40"
                                            aria-label="Export contract details"
                                            title="Export"
                                        >
                                            <Download size={15} />
                                        </button>
                                    }
                                    items={[
                                        {
                                            id: 'export-pdf',
                                            label: 'Export as PDF',
                                            icon: <FileText size={16} />,
                                            onClick: handleExportPdf,
                                        },
                                        {
                                            id: 'export-excel',
                                            label: 'Export as Excel',
                                            icon: <FileSpreadsheet size={16} />,
                                            onAsyncClick: handleExportExcel,
                                        },
                                    ]}
                                />
                            )}
                            <button
                                onClick={onClose}
                                className="flex items-center justify-center w-7 h-7 rounded-md hover:bg-[#f3f5f6] text-[#6d7d8a] transition-colors flex-shrink-0"
                                aria-label="Close contract details"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── Body — switches by state ── */}
                {state === 'loading' && <LoadingState />}
                {state === 'empty' && <EmptyState />}
                {state === 'error' && (
                    <ErrorState
                        error={
                            error ?? {
                                title: 'Unable to load contract',
                                message:
                                    'Something went wrong while retrieving contract details. Please try again.',
                            }
                        }
                        onRetry={onRetry}
                    />
                )}
                {state === 'ready' && <ReadyState products={products} />}
            </div>
        </>
    );
};
